import { type RootState } from "~/app/store";
import { selectDeepProductDescriptor } from "~/domains/product/redux/selectors";
import { createSelector } from "reselect";
import { type LineItem, isProductGroup, type EntryGroup } from "~/features/cart/cart/models/CartData";
import {
  selectIsCartAboveThreshold,
  selectLineItemsByGroupNumber,
} from "~/features/cart/cart/redux/selectors/cartSelectors";
import uniq from "lodash/uniq";
import { isKITProduct } from "~/domains/product/types/ProductData";
import { selectOrderableGroup, selectBundleGroups } from "~/features/cart/cart/redux/selectors/checkoutPageSelectors";

export interface InvalidCartItem {
  tableId: string;
  productGroupNumber: number;
}

const selectPackEntries = createSelector(
  (state: RootState) => state.cart.packageEntriesById,
  (packageEntriesById) => Object.values(packageEntriesById) as LineItem[],
);
const isInvalid = (item: LineItem) => !item.valid;
const isMissingPackSize = (item: LineItem) => item.packSize === undefined;
export const selectDisableCheckout = (externalDisableCheckout: boolean) => (state: RootState) => {
  if (externalDisableCheckout) {
    return true;
  }

  const packEntries = selectPackEntries(state);

  if (!selectIsCartAboveThreshold(state)) {
    if (packEntries.some(isMissingPackSize)) {
      return true;
    }
  }

  if (packEntries.some(isInvalid)) {
    return true;
  }
};
export const selectIsRequestInFlight = (state: RootState) => state.cartPage.requestInFlight;
export const selectAccountNumber = (state: RootState) => state.cartPage.accountNumber;
export const selectIsDstError = (state: RootState) => state.cartPage.dstError;
export const selectIsPnaLoading = createSelector(
  (state: RootState) => state.cart.packageEntriesById,
  (state: RootState) => state.products,
  (packageEntriesById, products) => {
    return (Object.values(packageEntriesById) as LineItem[]).some((packEntry) => {
      const productDescriptor = selectDeepProductDescriptor(products, {
        productCode: packEntry.productCode,
        priceReference: packEntry.priceReference,
      });
      return productDescriptor.isLoading;
    });
  },
);
export const selectCartContainsHousingKIT = createSelector(
  (state: RootState) => state.cart.packageEntriesById,
  (state: RootState) => state.products.basic,
  (packageEntriesById, basic) => {
    const productCodes = uniq((Object.values(packageEntriesById) as LineItem[]).map((e) => e.productCode));
    return productCodes.some((code) => isKITProduct(basic[code]));
  },
);

export const selectInvalidItems = createSelector(
  selectOrderableGroup,
  selectBundleGroups,
  selectLineItemsByGroupNumber,
  (state: RootState) => state.products,
  (orderableGroup, bundleGroups, lineItemsByGroupNumber, products): InvalidCartItem[] => {
    const invalidEntries: InvalidCartItem[] = [];

    const processGroup = (topGroup: NonNullable<typeof orderableGroup> | (typeof bundleGroups)[number]) => {
      const tableId = `table:${topGroup.groupType}:${topGroup.groupNumber}:${
        "quoteNumber" in topGroup ? topGroup.quoteNumber : ""
      }`;
      function walk(node: EntryGroup): void {
        if (isProductGroup(node)) {
          const groupLineItems = lineItemsByGroupNumber[node.groupNumber] ?? [];
          const hasInvalid = groupLineItems.some((e) => {
            const descriptor = selectDeepProductDescriptor(products, {
              productCode: e.productCode,
              priceReference: e.priceReference,
            });
            return !e.valid || descriptor.isError;
          });
          if (hasInvalid) {
            invalidEntries.push({ tableId, productGroupNumber: node.groupNumber });
          }
        }
        node.children.forEach(walk);
      }
      topGroup.children.forEach(walk);
    };

    if (orderableGroup) {
      processGroup(orderableGroup);
    }
    bundleGroups.forEach(processGroup);

    return invalidEntries;
  },
);
